import { Box, Button, Stack, TableCell, TableRow, Typography, useTheme } from "@mui/material"
import { useEffect, useState } from "react"
import { Case, Else, If, Switch } from "lib/@components/switch.js"
import { TruncatedTypography } from "lib/@components/truncated-typography.js"
import { setTypeReview } from "routes/asset-register/plugins/asset-mapping/ai-type-review/controller/set-type-review.js"
import { EditableTypeCell } from "routes/asset-register/plugins/asset-mapping/ai-type-review/components/editable-type-cell.js"
import { useBoundContext } from "lib/@components/binding/use-bound-context.js"
import { useAssetViewer } from "routes/asset-register/plugins/asset-mapping/use-asset-viewer.js"
import noop from "lib/noop"
import { IfTooltip } from "lib/if-tooltip.js"
import { PINNED_Z_INDEX } from "lib/@components/managed-columns/pinned-cell-styles"
import {
    REVIEW_STATUS_LABEL,
    userEditedTypeAndParent,
} from "routes/asset-register/plugins/asset-mapping/ai-type-review/review-status.js"

export function TypeReviewRow({ item, onReviewChange = noop }) {
    const theme = useTheme()
    const { busy } = useBoundContext()
    const [rowBusy, setRowBusy] = useState(false)
    const [reviewedTypeName, setReviewedTypeName] = useState(resolveDisplayedTypeName(item))
    const [reviewedParent, setReviewedParent] = useState(resolveDisplayedParent(item))
    const disabled = busy || rowBusy
    const userEditedRow = userEditedTypeAndParent(item)
    const hasSuggestedTypeChange = item.suggestedTypeName !== item.originalAssetType
    const suggestedParentCellKey = `suggested-parent-${item.typeKey}`
    const suggestedTypeCellKey = `suggested-type-${item.typeKey}`
    const viewAssets = useAssetViewer({ registers: [item.registerId], types: [item.typeKey] }, { canEdit: false })
    const hasAssets = (item.count ?? 0) > 0

    useEffect(syncReviewedValues, [
        item,
        item.reviewStatus,
        item.reviewedTypeName,
        item.suggestedTypeName,
        item.reviewedParent,
        item.suggestedParent,
    ])

    return (
        <TableRow
            data-cy="type-review-row"
            sx={{
                bgcolor: theme.palette.background.paper,
                "& > td": { verticalAlign: "top" },
                "& > td:first-of-type": { borderLeft: `4px solid ${getLeftBorderColor()}` },
            }}
        >
            <TableCell
                sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: PINNED_Z_INDEX.body,
                    bgcolor: theme.palette.background.paper,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    width: 180,
                }}
            >
                <Box sx={{ width: 1, minWidth: 0 }}>
                    <TruncatedTypography variant="body2" noWrap>
                        {item.originalAssetType}
                    </TruncatedTypography>
                </Box>
            </TableCell>
            <TableCell sx={{ width: 200 }}>
                <EditableTypeCell
                    value={reviewedTypeName || ""}
                    placeholder="No suggested asset type"
                    label="AI Suggested Asset Type"
                    disabled={disabled}
                    onConfirm={editTypeName}
                    itemTypeKey={suggestedTypeCellKey}
                    highlight={hasSuggestedTypeChange}
                />
            </TableCell>
            <TableCell sx={{ width: 180 }}>
                <EditableTypeCell
                    value={reviewedParent || ""}
                    placeholder="No suggested parent"
                    label="AI Suggested Parent"
                    disabled={disabled}
                    onConfirm={editParent}
                    itemTypeKey={suggestedParentCellKey}
                    highlight={hasSuggestedTypeChange}
                />
            </TableCell>
            <TableCell align="center" sx={{ width: 90 }}>
                <IfTooltip condition={hasAssets} title="View assets in this type">
                    <Button
                        size="small"
                        variant="text"
                        onClick={viewAssets}
                        disabled={!hasAssets}
                        data-cy={`type-review-view-assets-${item.typeKey}`}
                    >
                        {item.count ?? 0}
                    </Button>
                </IfTooltip>
            </TableCell>
            <TableCell sx={{ width: 160, overflow: "hidden" }}>
                <Box sx={{ width: 1, minWidth: 0 }}>
                    <TruncatedTypography variant="body2" noWrap>
                        {item.identifiedAsset}
                    </TruncatedTypography>
                </Box>
            </TableCell>

            <TableCell
                align="center"
                sx={{
                    position: "sticky",
                    right: 0,
                    zIndex: PINNED_Z_INDEX.body,
                    bgcolor: theme.palette.background.paper,
                    width: 190,
                    minWidth: 190,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                }}
            >
                <Switch value={item.reviewStatus}>
                    <Case when="approved">
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                        >
                            <Typography
                                color="success.main"
                                variant="body2"
                                fontWeight={600}
                                data-cy={`type-review-status-${item.typeKey}`}
                            >
                                <If value={userEditedRow}>
                                    {REVIEW_STATUS_LABEL.edited}
                                    <Else>{REVIEW_STATUS_LABEL.accepted}</Else>
                                </If>
                            </Typography>
                            <If value={!userEditedRow}>
                                <Button
                                    size="small"
                                    onClick={reject}
                                    disabled={disabled}
                                    color="error"
                                    variant="outlined"
                                    data-cy="type-review-reject"
                                >
                                    Reject
                                </Button>
                                <Else>
                                    <Button
                                        size="small"
                                        onClick={undo}
                                        disabled={disabled}
                                        color="inherit"
                                        variant="outlined"
                                        data-cy="type-review-reset"
                                    >
                                        Undo
                                    </Button>
                                </Else>
                            </If>
                        </Stack>
                    </Case>
                    <Case when="rejected">
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                        >
                            <Typography
                                color="error.main"
                                variant="body2"
                                fontWeight={600}
                                data-cy={`type-review-status-${item.typeKey}`}
                            >
                                {REVIEW_STATUS_LABEL.rejected}
                            </Typography>
                            <Button
                                size="small"
                                onClick={approve}
                                disabled={disabled}
                                color="inherit"
                                variant="outlined"
                                data-cy="type-review-approve"
                            >
                                Undo
                            </Button>
                        </Stack>
                    </Case>
                </Switch>
            </TableCell>
        </TableRow>
    )

    function syncReviewedValues() {
        setReviewedTypeName(resolveDisplayedTypeName(item))
        setReviewedParent(resolveDisplayedParent(item))
    }
    /**NOTE: EV Asset Type Suggestions table displays the AI's suggestion for type and parent (optionally edited by the user)
     * Rejected rows carry originalType / null in reviewedTypeName / reviewedParent for
     * downstream consumers (mapping, asset tables in regime+register, export) — those values must NOT be surfaced here.
     * Fall back to the AI suggestion instead.*/

    function resolveDisplayedTypeName(rowItem) {
        if (rowItem.reviewStatus === "rejected") return rowItem.suggestedTypeName
        return rowItem.reviewedTypeName ?? rowItem.suggestedTypeName
    }

    function resolveDisplayedParent(rowItem) {
        if (rowItem.reviewStatus === "rejected") return rowItem.suggestedParent
        return rowItem.reviewedParent ?? rowItem.suggestedParent
    }

    function getLeftBorderColor() {
        switch (item.reviewStatus) {
            case "approved":
                return theme.palette.success.main
            case "rejected":
                return theme.palette.error.main
            default:
                return "transparent"
        }
    }

    async function editParent(newParent) {
        setReviewedParent(newParent)
        await saveReview("approved", reviewedTypeName, newParent)
    }

    async function editTypeName(newTypeName) {
        setReviewedTypeName(newTypeName)
        await saveReview("approved", newTypeName, reviewedParent)
    }

    async function approve() {
        await saveReview("approved", reviewedTypeName, reviewedParent)
    }

    async function reject() {
        await saveReview("rejected", item.originalAssetType, null)
    }

    async function undo() {
        await saveReview("approved", item.suggestedTypeName, item.suggestedParent)
    }

    async function saveReview(reviewStatus, nextReviewedTypeName, nextReviewedParent) {
        const previousReviewOverride = {
            reviewStatus: item.reviewStatus,
            reviewedTypeName,
            reviewedParent,
        }

        onReviewChange(item.typeKey, {
            reviewStatus,
            reviewedTypeName: nextReviewedTypeName,
            reviewedParent: nextReviewedParent,
        })
        const busyTimer = setTimeout(() => setRowBusy(true), 300)
        try {
            await setTypeReview({
                id: item.registerId,
                type: item.typeKey,
                reviewStatus,
                reviewedTypeName: nextReviewedTypeName,
                reviewedParent: nextReviewedParent,
            })
        } catch (error) {
            onReviewChange(item.typeKey, previousReviewOverride)
            throw error
        } finally {
            clearTimeout(busyTimer)
            setRowBusy(false)
        }
    }
}
